import { ElementController, getData, select } from 'js-common';
import A11yDialog from 'a11y-dialog';

import { SLIDER_INTERACTING_CLASS } from './ArticleSlider';

// ID of page content that will be hidden when the modal is open.
const PAGE_CONTENT_ID = 'page-content';

// ID set on the first heading in the modal, used for aria-labelledby
const MODAL_TITLE_ID = 'modal-title';

// Class name that is toggled on <html> when the modal is open
const MODAL_OPEN_CLASS_NAME = 'modal-open';

// Attribute with an a group's number of items, used for prev/next
const MODAL_GROUP_ITEM_COUNT_DATA_ATTRIBUTE = 'modal-group-item-count';

// Attribute with an item's index in a group, used for prev/next
const MODAL_ITEM_INDEX_DATA_ATTRIBUTE = 'modal-item-index';

// See docblock
const MODAL_GROUP_DATA_ATTRIBUTE = 'modal-group';
const DYNAMIC_MODAL_GROUP_DATA_ATTRIBUTE = 'modal-group-dynamic';
const MODAL_TRIGGER_DATA_ATTRIBUTE = 'modal-trigger';
const MODAL_CONTENT_DATA_ATTRIBUTE = 'modal-content';
const MODAL_CLASS_DATA_ATTRIBUTE = 'modal-class';

/**
 * Controller for article modals using a11y-dialog.
 *
 * The controller is bound to the modal element (only one in the document) and
 * manually finds the groups and their triggers with regular selectors.
 *
 * Has a number of requirements for function and accessibility:
 *
 * - The 'page content ID' element must be a sibling to the modal, not a parent.
 *   It will be hidden with `aria-hidden` and having the modal as a child would
 *   make it hidden too.
 * - The *group* acts as a boundary for items inside it regarding the previous
 *   and next buttons. It's also used to bind the events, so all items must
 *   be contained inside a group.
 * - The 'item' inside a group is defined by the element with the *content*
 *   attribute and is a container for all triggers for that content. When the
 *   modal is opened, the item's content attribute will be set as the modal's
 *   body.
 * - The *trigger* inside an item is an element (probably <a> or <button>) that
 *   shows the modal with the content set from its item parent/ancestor.
 * - Any triggers that are anchor (<a>) elements are 'converted' to buttons
 *   with the help of `role` and a keyboard event listener.
 *
 * An example that isn't related to this implementation as such, but covers
 * the requirements:
 * https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html
 *
 * @extends {ElementController}
 */
export default class ArticleModal extends ElementController {
  static id = 'ArticleModal';

  initialState = {
    currentGroup: null,
    currentGroupCount: 0,
    currentIndex: 0,
    isOpen: false,
    pageTitle: document.title,
    pageUrl: window.location.href,
  };

  /**
   * DOM loaded, run initial modal setup and bind events.
   */
  init() {
    // Initial state
    this.setState(this.initialState);

    // Init modal
    this.modal = new A11yDialog(
      this.getRoot(),
      document.getElementById(PAGE_CONTENT_ID),
    );

    // Save elements
    this.modalBody = this.getSingleNode('body');
    this.modalContent = this.getSingleNode('content');
    this.modalClose = this.getSingleNode('close');
    this.modalPrev = this.getSingleNode('prev');
    this.modalNext = this.getSingleNode('next');
    this.modalGroups = select(`[data-${MODAL_GROUP_DATA_ATTRIBUTE}]`);
    this.dynamicModalGroups = this.modalGroups.filter((group) =>
      group.hasAttribute(`data-${DYNAMIC_MODAL_GROUP_DATA_ATTRIBUTE}`),
    );

    setTimeout(this.delayedInit, 250);

    this.bindEvents();
  }

  /**
   * Init after a short timeout, to give other modules time to possibly move
   * elements around.
   */
  delayedInit = () => {
    this.buttonizeLinks();
    this.modalGroups.forEach(this.setPrevNextData);

    // Add mutation observer if relevant
    if (this.dynamicModalGroups.length) {
      this.observers = new WeakMap();
      this.dynamicModalGroups.forEach(this.observeGroupChildren);
    }
  };

  /**
   * Bind all required event listeners.
   */
  bindEvents() {
    // React to open/close
    this.modal.on('show', this.onModalOpen);
    this.modal.on('hide', this.onModalClose);

    // Monitor control keys for opening in a new tab
    this.on(document.body, 'keydown', this.handleBodyKeydown);
    this.on(document.body, 'keyup', this.handleBodyKeyup);

    // The overlay is covered by the body and can't be clicked, so it must
    // be handled manually
    this.on(this.modalBody, 'click', this.handleModalBodyClick);

    // Handle arrow keys for prev/next
    this.on(this.modalBody, 'keyup', this.handleModalBodyKeyup);

    // Bind events for each group
    this.on(
      this.modalGroups,
      'click',
      `[data-${MODAL_TRIGGER_DATA_ATTRIBUTE}]`,
      this.handleTriggerClick,
    );

    // Second part of buttonizeLinks, listen to space
    this.on(
      this.modalGroups,
      // Must be keydown rather than keyup to prevent a space press from
      // scrolling the page
      'keydown',
      `[data-${MODAL_TRIGGER_DATA_ATTRIBUTE}]`,
      this.handleTriggerKeydown,
    );

    // Handle prev/next
    this.on(this.modalPrev, 'click', this.handlePrevClick);
    this.on(this.modalNext, 'click', this.handleNextClick);

    // Handle browser history
    this.on(window, 'popstate', this.handlePopstate);
  }

  /**
   * Make modal trigger links behave like buttons.
   */
  buttonizeLinks() {
    // Scoping the selector to an element type seems to be much slower, check
    // the node type later instead.
    select(`[data-${MODAL_TRIGGER_DATA_ATTRIBUTE}]`).forEach((trigger) => {
      if (trigger.nodeName === 'A') {
        trigger.setAttribute('role', 'button');
      }
    });
  }

  /**
   * Disable or enable prev/next buttons when relevant.
   */
  checkPrevNextButtons() {
    if (!this.state.isOpen || !this.state.currentGroupCount) {
      return;
    }

    if (this.state.currentIndex === 0) {
      this.modalPrev.disabled = true;
    } else {
      this.modalPrev.disabled = false;
    }

    if (this.state.currentIndex === this.state.currentGroupCount - 1) {
      this.modalNext.disabled = true;
    } else {
      this.modalNext.disabled = false;
    }
  }

  /**
   * Get a group item selector based on its index.
   *
   * @param {number} itemIndex - Item index inside the group.
   * @return {string}
   */
  getItemIndexSelector(itemIndex) {
    return `[data-${MODAL_ITEM_INDEX_DATA_ATTRIBUTE}="${itemIndex}"]`;
  }

  /**
   * Set data attributes used for prev/next navigation.
   *
   * @param {Object} group - DOM node for the group to process.
   */
  setPrevNextData(group) {
    const groupItems = Array.from(
      group.querySelectorAll(`[data-${MODAL_CONTENT_DATA_ATTRIBUTE}]`),
    );
    group.setAttribute(
      `data-${MODAL_GROUP_ITEM_COUNT_DATA_ATTRIBUTE}`,
      groupItems.length,
    );
    groupItems.forEach((item, i) => {
      item.setAttribute(`data-${MODAL_ITEM_INDEX_DATA_ATTRIBUTE}`, i);
    });
  }

  /**
   * Set the modal's content.
   *
   * @param {Object} groupItem - Item that has the content data attribute.
   * @param {Object} trigger - Node that triggered the opening.
   */
  setModalContent(groupItem, trigger) {
    this.modalContent.innerHTML = getData(
      groupItem,
      MODAL_CONTENT_DATA_ATTRIBUTE,
    );

    // Set extra class
    const extraClass = getData(groupItem, MODAL_CLASS_DATA_ATTRIBUTE);
    if (this.lastExtraClass) {
      this.modalBody.classList.remove(this.lastExtraClass);
      this.lastExtraClass = '';
    }
    if (extraClass) {
      this.modalBody.classList.add(extraClass);
      this.lastExtraClass = extraClass;
    }

    // Add id to heading for aria-labelledby
    const firstHeading = this.modalContent.querySelector('h1, h2');
    if (firstHeading) {
      firstHeading.id = MODAL_TITLE_ID;
    }

    // Set browser history when on the same origin, trying to set a different
    // origin results in a SecurityError.
    if (trigger && trigger.href.indexOf(window.location.origin) === 0) {
      const openedName = trigger.textContent;
      const suffix = window.APP_DATA ? window.APP_DATA.titleSuffix : '';
      document.title = `${openedName} ${suffix}`;
      // Keep a single history entry. If there is no current history state,
      // the modal is opened from the page and the history should be pushed.
      // Otherwise if there already is a URL set, the modal is open and the
      // history should be replaced.
      const historyMethod =
        !window.history.state || !window.history.state.cardUrl
          ? 'pushState'
          : 'replaceState';
      window.history[historyMethod](
        { cardUrl: trigger.href },
        openedName,
        trigger.href,
      );
    }
  }

  /**
   * Set modal content based on the specified item index.
   *
   * @param {number} index - Modal item index in the current group.
   */
  setModalContentByIndex(index) {
    if (!this.state.currentGroup || !this.state.currentGroupCount) {
      throw new Error('Must have group data to set by index');
    }

    const item = this.state.currentGroup.querySelector(
      this.getItemIndexSelector(index),
    );
    if (!item) {
      throw new Error(`No item with index ${index}`);
    }

    this.setModalContent(
      item,
      item.querySelector(`a[data-${MODAL_TRIGGER_DATA_ATTRIBUTE}]`),
    );
  }

  /**
   * Show previous item's content if possible.
   */
  showPrevContent() {
    const newIndex = this.state.currentIndex - 1;
    if (newIndex < 0) {
      return;
    }
    this.setModalContentByIndex(newIndex);
    this.setState({ currentIndex: newIndex });
    this.checkPrevNextButtons();
  }

  /**
   * Show next item's content if possible.
   */
  showNextContent() {
    const newIndex = this.state.currentIndex + 1;
    if (newIndex > this.state.currentGroupCount - 1) {
      return;
    }
    this.setModalContentByIndex(newIndex);
    this.setState({ currentIndex: newIndex });
    this.checkPrevNextButtons();
  }

  /**
   * Set the modal content and open the modal.
   *
   * @param {Object} trigger - The element that triggered the open.
   * @param {Object} [group] - The trigger element's group, pass if already
   *   selected to avoid an extra DOM lookup.
   */
  openModal(trigger, group) {
    if (this.state.isOpen) {
      return;
    }

    // Insert the content
    const item = trigger.closest(`[data-${MODAL_CONTENT_DATA_ATTRIBUTE}]`);
    this.setModalContent(item, trigger);

    this.modal.show();

    // Set current data for prev/next
    const currentGroup =
      group || trigger.closest(`[data-${MODAL_GROUP_DATA_ATTRIBUTE}]`);
    this.setState({
      isOpen: true,
      currentGroup,
      currentGroupCount: getData(
        currentGroup,
        MODAL_GROUP_ITEM_COUNT_DATA_ATTRIBUTE,
      ),
      currentIndex: getData(item, MODAL_ITEM_INDEX_DATA_ATTRIBUTE),
    });

    this.checkPrevNextButtons();
  }

  /**
   * Close the modal if it's open.
   */
  closeModal() {
    if (this.state.isOpen) {
      this.modal.hide();
    }
  }

  /**
   * Keep track if control keys are pressed.
   *
   * @param {Object} e - Keydown event.
   */
  handleBodyKeydown = (e) => {
    if (e.key === 'Meta' || e.key === 'Control') {
      this.setState({ isHoldingControlKey: true });
    }
  };

  /**
   * Keep track if control keys are pressed.
   *
   * @param {Object} e - Keyup event.
   */
  handleBodyKeyup = (e) => {
    if (e.key === 'Meta' || e.key === 'Control') {
      this.setState({ isHoldingControlKey: false });
    }
  };

  /**
   * Close the modal if clicking in the modal body but outside the modal
   * content.
   *
   * @param {Object} e - Click event.
   */
  handleModalBodyClick = (e) => {
    // The event `target` will be the element that first got the click, so a
    // click inside the content will not result in the body being the target.
    if (e.target === this.modalBody) {
      this.modalClose.click();
    }
  };

  /**
   * Navigate within the group when pressing left/right arrow keys.
   *
   * @param {Object} e - Keyup event.
   */
  handleModalBodyKeyup = (e) => {
    if (e.key === 'ArrowLeft') {
      this.showPrevContent();
    } else if (e.key === 'ArrowRight') {
      this.showNextContent();
    }
  };

  /**
   * Set a body class when the modal opens.
   */
  onModalOpen = () => {
    document.documentElement.classList.add(MODAL_OPEN_CLASS_NAME);
  };

  /**
   * Remove the 'open' body class and reset state when the modal closes.
   */
  onModalClose = () => {
    document.documentElement.classList.remove(MODAL_OPEN_CLASS_NAME);
    document.title = this.state.pageTitle;

    if (this.lastExtraClass) {
      this.modalBody.classList.remove(this.lastExtraClass);
      this.lastExtraClass = '';
    }

    // If the modal is closed with the close button, the current history state
    // will still be as if it's not. Go back to the page that opened the modal.
    if (
      window.history.state &&
      window.history.state.cardUrl &&
      window.history.state.cardUrl !== this.state.pageUrl
    ) {
      window.history.back();
    }

    this.setState(this.initialState);
  };

  /**
   * Open the modal on click.
   *
   * @param {Object} e - Click event.
   */
  handleTriggerClick = (e) => {
    // The slider lib doesn't prevent clicks when dragging, so check that
    // it doesn't have a 'currently interacting' class before opening.
    const trigger = e.currentTarget;
    const group = trigger.closest(`[data-${MODAL_GROUP_DATA_ATTRIBUTE}]`);
    const isInActiveSlide = group.classList.contains(SLIDER_INTERACTING_CLASS);
    // Don't trigger modal if trying to open in a new tab.
    if (!this.state.isHoldingControlKey || isInActiveSlide) {
      e.preventDefault();
    }
    if (!isInActiveSlide && !this.state.isHoldingControlKey) {
      this.openModal(trigger, group);
    }
  };

  /**
   * Open the modal if control keys are pressed.
   *
   * @param {Object} e - Keydown event.
   */
  handleTriggerKeydown = (e) => {
    // Buttons can be activated with enter or space. Ctrl/Cmd + Enter is for
    // opening in a new tab just like for click.
    if (
      (e.key === 'Enter' && !this.state.isHoldingControlKey) ||
      e.key === ' '
    ) {
      e.preventDefault();
      this.openModal(e.currentTarget);
    }
  };

  /**
   * Show previous item's content if possible.
   */
  handlePrevClick = () => {
    this.showPrevContent();
  };

  /**
   * Show next item's content if possible.
   */
  handleNextClick = () => {
    this.showNextContent();
  };

  /**
   * Set prev/next data again if a group's children has changed.
   *
   * @param {Array.<MutationRecord>} mutations - All observed group mutations.
   */
  onGroupChildrenMutation = (mutations) => {
    // The observer is only observing groups, so just getting the target from
    // the last one rather than iterating over all mutations should be fine.
    const lastMutation = mutations[mutations.length - 1];
    const group = lastMutation.target;
    this.setPrevNextData(group);
  };

  /**
   * Observe child node changes for the specified group.
   *
   * @param {Object} group - DOM node for group to observe.
   */
  observeGroupChildren = (group) => {
    const observer = new MutationObserver(this.onGroupChildrenMutation);
    observer.observe(group, { childList: true });
    this.observers.set(group, observer);
  };

  /**
   * Open or close the modal when navigating the browser history.
   *
   * @param {Object} e - Popstate event.
   */
  handlePopstate = (e) => {
    if (this.state.isOpen) {
      // Going back from an opened modal, close it
      this.closeModal();
    } else if (e.state && e.state.cardUrl) {
      // Going forward to a previously opened modal, open it again. Must find
      // all triggers and check the href *property* since that always returns
      // the full URL. Doing an attribute selection from the URL will fail
      // if the href *attribute* uses a relative URL.
      // Using some rather than forEach to stop as soon as a match is found.
      select(`a[data-${MODAL_TRIGGER_DATA_ATTRIBUTE}]`).some((trigger) => {
        if (trigger.href === e.state.cardUrl) {
          this.openModal(trigger);
          return true;
        }
        return false;
      });
    } else if (!e.state && !this.modalGroups.length) {
      // Browsers keep the history state so after opening a modal and refreshing
      // the page with the new URL, it still be a 'history entry' rather than
      // a 'regular browser entry'. The browser will not do anything with that
      // when going back from it other than update the address bar.
      window.location.reload();
    }
  };
}
